import {
  Stack,
  Box,
  Typography,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { DateRange } from "models/interfaces";
import { useEffect, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import EmailIcon from "@mui/icons-material/Email";
import { ApexOptions } from "apexcharts";
import {
  calculateDaysBetween,
  getFormattedStatsCount,
  handleErrorMessage,
  last30DaysFromDate,
  last30DaysToDate,
} from "helpers/methods";
import { fetchEmailSent, fetchEmailSentGraph } from "../DashboardService";
import DashboardDateRangePicker from "./DashboardDateRangePicker";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";

const EmailSentOverView = () => {
  const [chartOptions, setChartOptions] = useState<ApexOptions>({
    chart: {
      toolbar: { show: true },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.2,
        opacityFrom: 0.3,
        opacityTo: 0.1,
        stops: [20, 100],
      },
    },
    tooltip: {
      enabled: true,
      theme: "dark",
      y: {
        formatter: (value: any) => value.toFixed(0),
      },
    },
    grid: {
      borderColor: "#e0e0e0",
      strokeDashArray: 4,
    },
    xaxis: {
      categories: [],
      labels: {
        style: {
          fontFamily: "Inter",
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => value.toFixed(0),
        style: {
          fontFamily: "Inter",
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    colors: ["#7551D0"], // Line color
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [series, setSeries] = useState<any[]>([]);
  const [sentEmails, setSentEmails] = useState<any>([]);
  const [openEmailDateRangePicker, setOpenEmailDateRangePicker] =
    useState<boolean>(false);
  const [emailDateRange, setEmailDateRange] = useState<DateRange>({
    fromDate: last30DaysFromDate,
    toDate: last30DaysToDate,
  });

  const dateRangeRef = useRef(emailDateRange);

  useEffect(() => {
    dateRangeRef.current = emailDateRange;
  }, [emailDateRange, setEmailDateRange]);

  useEffect(() => {
    getEmailSentDateWiseCount(emailDateRange);
  }, []);

  const getEmailSentDateWiseCount = async (emailDateRange: DateRange) => {
    try {
      setLoading(true);
      const [sentEmailsCounts, graphData] = await Promise.all([
        fetchEmailSent(emailDateRange.fromDate, emailDateRange.toDate, ""),
        fetchEmailSentGraph(emailDateRange.fromDate, emailDateRange.toDate, ""),
      ]);
      setSentEmails(sentEmailsCounts);
      const allDates = Object.keys(graphData).map((date) =>
        new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
        })
      );

      const dataValues = Object.values(graphData);
      setSeries([{ name: "Contacts", data: dataValues }]);
      setChartOptions((prevOptions) => ({
        ...prevOptions,
        xaxis: {
          ...prevOptions.xaxis,
          categories: allDates,
          tickAmount: Math.ceil(allDates.length / 2),
        },
      }));
      setSeries([{ name: "Count", data: dataValues }]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const getPercentage = (count: number, divideby: number) => {
    return ((count / (divideby === 0 ? 1 : divideby)) * 100).toFixed();
  };

  const getEmailCounts = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <EmailIcon sx={{ color: "#B9BABB", pr: 1 }} />
            <Typography variant="h4">Emails sent</Typography>
          </Box>
          <DashboardDateRangePicker
            openDateRangePicker={openEmailDateRangePicker}
            setOpenDateRangePicker={setOpenEmailDateRangePicker}
            dateRange={emailDateRange}
            setDateRange={setEmailDateRange}
            helperFunction={getEmailSentDateWiseCount}
          />
        </Stack>
        <Divider />
        <Stack>
          <Box p={2}>
            <Stack spacing={1}>
              <Typography>
                Last {calculateDaysBetween(emailDateRange)} days emails sent
              </Typography>
              <Typography variant="h5">
                {getFormattedStatsCount(sentEmails[0]?.sentCount)}
              </Typography>
            </Stack>
            <Grid container columnSpacing={3} mt={3}>
              <Grid item lg={4} xl={4} md={6} sm={12} xs={12}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight={500}>
                        Open rate
                      </Typography>
                      <CustomInfoTooltip
                        placement="bottom"
                        infoMessage={
                          "Shows how many recipients opened your email, helping you assess subject line effectiveness and audience engagement."
                        }
                      />
                      {/* <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          borderBottom: "1px dashed #B9BABB",
                        }}
                      >
                        <Typography variant="body2">
                          {sentEmails[0]?.openedCount}
                        </Typography>
                      </Stack> */}
                    </Stack>
                    <Typography variant="h5" color="primary">
                      {getPercentage(
                        sentEmails[0]?.openedCount || 0,
                        sentEmails[0]?.successCount || 0
                      )}
                      %
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={parseInt(
                      getPercentage(
                        sentEmails[0]?.openedCount || 0,
                        sentEmails[0]?.successCount || 0
                      )
                    )}
                    sx={{
                      height: "8px",
                      mt: 1,
                      backgroundColor: "#47556940",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#91CD26",
                      },
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item lg={4} xl={4} md={6} sm={12} xs={12}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight={500}>
                        Click rate
                      </Typography>
                      <CustomInfoTooltip
                        placement="bottom"
                        infoMessage={
                          "Tracks engagement by counting recipients who clicked on a link in your email. A higher rate indicates strong interest."
                        }
                      />
                      {/* <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          borderBottom: "1px dashed #B9BABB",
                        }}
                      >
                        <Typography variant="body2">
                          {sentEmails[0]?.clickedCount}
                        </Typography>
                      </Stack> */}
                    </Stack>
                    <Typography variant="h5" color="primary">
                      {getPercentage(
                        sentEmails[0]?.clickedCount || 0,
                        sentEmails[0]?.successCount || 0
                      )}
                      %
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={parseInt(
                      getPercentage(
                        sentEmails[0]?.clickedCount || 0,
                        sentEmails[0]?.successCount || 0
                      )
                    )}
                    sx={{
                      height: "8px",
                      mt: 1,
                      backgroundColor: "#47556940",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#91CD26",
                      },
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item lg={4} xl={4} md={6} sm={12} xs={12}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight={500}>
                        Click to open rate
                      </Typography>
                      <CustomInfoTooltip
                        placement="bottom"
                        infoMessage={
                          "Measures how effective your email content is by showing the percentage of those who clicked after opening (Click Rate / Open Rate)."
                        }
                      />
                    </Stack>
                    <Typography variant="h5" color="primary">
                      {getPercentage(
                        sentEmails[0]?.clickedCount || 0,
                        sentEmails[0]?.openedCount || 0
                      )}
                      %
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={parseInt(
                      getPercentage(
                        sentEmails[0]?.clickedCount || 0,
                        sentEmails[0]?.openedCount || 0
                      )
                    )}
                    sx={{
                      height: "8px",
                      mt: 1,
                      backgroundColor: "#47556940",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#91CD26",
                      },
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Stack>
        <Stack>
          <Box px={2}>
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="area"
              height={300}
            />
          </Box>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getAdditionalMetricsSkeleton = () => {
    return (
      <>
        <Grid container spacing={3}>
          {Array(3)
            .fill(0)
            .map((_, index) => (
              <Grid key={index} item xs={12} sm={12} md={4} lg={4} xl={4}>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between">
                    <Skeleton variant="text" width="100%" height={24} />
                  </Stack>
                </Stack>
              </Grid>
            ))}
        </Grid>
      </>
    );
  };

  const getSubscriberCountsSkeleton = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <Skeleton variant="text" width={100} height={32} />
          </Box>
          <Skeleton variant="rounded" width={200} height={40} />
        </Stack>
        <Divider />
        <Stack>
          <Box p={2}>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="text" width={80} height={32} />
          </Box>
        </Stack>
        <Stack>
          <Box p={2}>{getAdditionalMetricsSkeleton()}</Box>
          <Box p={2}>
            <Skeleton variant="rounded" width="100%" height={250} />
          </Box>
        </Stack>
      </CenteredCardLayout>
    );
  };

  return <>{loading ? getSubscriberCountsSkeleton() : getEmailCounts()}</>;
};

export default EmailSentOverView;
